import React, { useEffect, useRef, useState } from 'react';

import { getHighlightedText, getLangCountryPrefix, resolveArguments } from 'helpers/utils';

import * as Sentry from '@sentry/react';

import { MobileHelpLink } from './styles';
import { HELP_PAYSEND } from '../../../constants/UrlConstants';
import { LightIcon } from '../common/fromAuth/LightIcon/LightIcon';
import { LightNavBar } from '../common/fromAuth/LightNavBar/LightNavBar';
import { LightText } from '../common/fromAuth/LightText/LightText';
import { PromoEmptyPage } from '../form/PromoInputPhoneWithDropdown/PromoEmptyPage';
import { PromoSearchBar } from '../form/PromoInputPhoneWithDropdown/PromoSearchBar';
import { getHelpCenterUrl, getZendeskLocale, useDebounce, useGetQueryParams } from '../utils';
import PromoModalComponent from '../utils/PromoModalComponent';

const searchArticles = async (lang, country, query) => {
    const href = `${HELP_PAYSEND}/api/v2/help_center/articles/search.json?query=${query}&locale=${getZendeskLocale(lang, country)}`;
    try {
        const resp = await fetch(href, { method: 'GET' });
        if (resp.status === 200) {
            const json = await resp.json();
            return json;
        }
    } catch (e) {
        Sentry.captureException(e, {
            level: 'log',
        });
        return null;
    }
};

const getArticles = async (lang, country, query, sections) => {
    const articles = await searchArticles(lang, country, query);

    if (articles) {
        return articles.results.map((article) => {
            const section = sections.find((s) => s.id === article.section_id);
            return {
                id: article.id,
                title: article.title,
                section_id: article.section_id,
                category_id: section.category_id,
                directLink: article.label_names?.includes('directLink'),
            };
        });
    }

    return null;
};

export const ZendeskHelpMobileSearchModal = (props) => {
    const urlParams = useGetQueryParams();
    const searchRef = useRef(null);

    const [search, setSearch] = useState('');
    const [foundArticles, setFoundArticles] = useState({
        articlesList: [],
        query: '',
    });

    const url = `${getLangCountryPrefix(props.lang, props.country)}/help-mobile`;

    const debouncedSearch = useDebounce(search, 1000);

    useEffect(() => {
        const fetchData = async (query) => {
            if (!query) {
                return;
            }

            const articles = await getArticles(props.lang, props.country, query, props.sections);

            if (articles) {
                const filteredArticles = articles.filter((article) => !article.directLink);
                setFoundArticles({
                    articlesList: filteredArticles,
                    query: query,
                });
            }
        };

        fetchData(debouncedSearch);
    }, [debouncedSearch]);

    const articlesNames = foundArticles?.articlesList?.map((article) => {
        return (
            <MobileHelpLink
                key={article.id}
                href={getHelpCenterUrl(`${url}/category/${article.category_id}/section/${article.section_id}/article/${article.id}`, urlParams)}
            >
                {!!foundArticles?.query ? (
                    <span>{getHighlightedText(foundArticles?.query, article.title, 'lightText', {}, props.useThemeColor)}</span>
                ) : (
                    <LightText useThemeColor>{article.title}</LightText>
                )}
                <LightIcon useThemeColor name="chevron-right" color={'purple'} leftIndent={'16px'} />
            </MobileHelpLink>
        );
    });

    return (
        <PromoModalComponent canCloseModal={false} useThemeColor={props.useThemeColor}>
            {articlesNames?.length === 0 && search && (
                <PromoEmptyPage
                    showChat
                    chatButton={props.chatButton}
                    chatButtonType={props.chatButtonType}
                    chatButtonHref={props.chatButtonHref}
                    toggleChat={props.toggleChat}
                    notFoundMessage={resolveArguments(props.searchNoResult, search)}
                    useThemeColor={props.useThemeColor}
                />
            )}
            {articlesNames?.length > 0 && search && <div>{articlesNames}</div>}
            <LightNavBar isSearchBar>
                <PromoSearchBar
                    searchRef={searchRef}
                    placeholder={props.searchPlaceholder}
                    onChange={(e) => setSearch(e.target.value)}
                    onCloseButtonClick={props.toggleSearch}
                    useThemeColor={props.useThemeColor}
                />
            </LightNavBar>
        </PromoModalComponent>
    );
};
